import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollTop from "@/components/feature/ScrollTop";
import TopicCard from "@/components/feature/TopicCard";
import RankSidebar from "@/components/feature/RankSidebar";
import FaqAccordion from "@/components/feature/FaqAccordion";
import { channels } from "@/mocks/channels";
import { topics } from "@/mocks/topics";
import { quotes, faqs } from "@/mocks/extra";
import { formatNum } from "@/lib/format";
import { useSeo } from "@/hooks/useSeo";

const sorted = [...topics].sort((a, b) => b.heat - a.heat);
const featured = sorted[0];
const second = sorted[1];
const third = sorted[2];
const feedAll = sorted.slice(3);

const tickerItems = sorted.slice(0, 6).map((t) => t.title);

export default function Home() {
  const [active, setActive] = useState<string>("all");
  useSeo({
    title: "全网吃瓜 - 网友评论围观 · 舆论风向与热议观点速览",
    description:
      "全网吃瓜：不用自己翻评论，汇集评论区真实声音、热议观点与舆论风向，快速了解热门事件的主流看法与情绪走向。",
    keywords: "全网吃瓜,网友评论,舆论风向,热议观点,群众声音",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: "全网吃瓜",
        url: "https://coyonmachine.cn/",
        potentialAction: {
          "@type": "SearchAction",
          target: "https://coyonmachine.cn/rank?q={search_term_string}",
          "query-input": "required name=search_term_string",
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  });

  const feed = active === "all" ? feedAll : feedAll.filter((t) => t.channel === active);
  const activeCh = channels.find((c) => c.slug === active);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* ===== 首屏 ===== */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=playful%20editorial%20collage%20of%20speech%20bubbles%20chat%20icons%20and%20rising%20trending%20arrows%20in%20coral%20orange%20and%20cream%20tones%2C%20abstract%20warm%20newspaper%20style%20illustration%2C%20busy%20yet%20balanced%20composition%2C%20soft%20layered%20shapes%2C%20modern%20magazine%20cover%20vibe&width=1600&height=900&seq=cg-hero-home&orientation=landscape"
            alt="全网吃瓜 - 网友评论与热议观点聚合"
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/80 via-foreground-950/55 to-primary-900/40" />
        </div>

        <div className="wrap relative flex min-h-[560px] flex-col justify-center py-16 md:min-h-[620px]">
          <div className="w-full max-w-3xl animate-fade-up">
            <span className="inline-flex items-center gap-2 rounded-full border border-background-50/30 bg-background-50/10 px-3 py-1.5 text-sm text-background-50 backdrop-blur-sm">
              <i className="ri-fire-fill text-accent-400" aria-hidden="true" />
              今日全网热议进行时
            </span>
            <h1 className="mt-5 font-display text-4xl font-bold leading-tight text-background-50 md:text-6xl">
              不翻评论，
              <br className="md:hidden" />
              直接看<span className="text-accent-300">群众怎么想</span>
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-background-100/90 md:text-lg">
              全网吃瓜汇集评论区真实声音、热议观点与舆论风向，把吵来吵去的评论区，
              整理成你一分钟就能看懂的主流看法与情绪走向。
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
              <Link
                to="/rank"
                className="flex items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-primary-500 px-6 py-3.5 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600"
              >
                去围观热议榜
                <i className="ri-arrow-right-line" aria-hidden="true" />
              </Link>
              <Link
                to="/about"
                className="flex items-center justify-center gap-2 whitespace-nowrap rounded-lg border border-background-50/40 bg-background-50/10 px-6 py-3.5 text-sm font-semibold text-background-50 backdrop-blur-sm transition-colors hover:bg-background-50/20"
              >
                了解我们
              </Link>
            </div>

            <div className="mt-10 grid max-w-xl grid-cols-3 gap-4 border-t border-background-50/20 pt-6">
              <div>
                <p className="font-display text-2xl font-bold text-background-50 md:text-3xl">38.6万</p>
                <p className="mt-1 text-xs text-background-200/80">今日新增讨论</p>
              </div>
              <div>
                <p className="font-display text-2xl font-bold text-background-50 md:text-3xl">1200+</p>
                <p className="mt-1 text-xs text-background-200/80">已收录热议话题</p>
              </div>
              <div>
                <p className="font-display text-2xl font-bold text-background-50 md:text-3xl">24h</p>
                <p className="mt-1 text-xs text-background-200/80">全天候风向追踪</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== 跑马灯快讯 ===== */}
      <div className="flex items-stretch overflow-hidden border-b border-background-200 bg-background-50">
        <div className="z-10 flex shrink-0 items-center gap-2 bg-primary-500 px-4 py-3 text-sm font-bold text-background-50 md:px-6">
          <i className="ri-flashlight-fill" aria-hidden="true" />
          <span className="hidden sm:inline">正在热议</span>
        </div>
        <div className="relative flex-1 overflow-hidden">
          <div className="flex w-max animate-marquee items-center gap-10 py-3 pl-6">
            {[...tickerItems, ...tickerItems].map((txt, i) => (
              <span key={i} className="flex items-center gap-2 whitespace-nowrap text-sm text-foreground-700">
                <span className="h-1.5 w-1.5 rounded-full bg-accent-500" />
                {txt}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* ===== 今日舆论风向 ===== */}
      <section className="wrap pt-12 md:pt-16">
        <div className="mb-8 flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-end">
          <div>
            <span className="flex items-center gap-2 text-sm font-bold text-primary-600">
              <i className="ri-compass-3-line" aria-hidden="true" />
              今日舆论风向
            </span>
            <h2 className="mt-2 font-display text-3xl font-bold text-foreground-900">
              现在大家都在怎么看？
            </h2>
          </div>
          <p className="max-w-sm text-sm text-foreground-500">
            三个当下热度最高的讨论，浓缩呈现各方态度与情绪走向。
          </p>
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          {/* 头条大卡 */}
          <FeaturedTrend topic={featured} />
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
            <CompactTrend topic={second} />
            <CompactTrend topic={third} />
          </div>
        </div>
      </section>

      {/* ===== 频道广场 ===== */}
      <section className="mt-14 bg-background-100 py-14 md:py-16">
        <div className="wrap">
          <div className="mb-8 flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-end">
            <div>
              <span className="flex items-center gap-2 text-sm font-bold text-primary-600">
                <i className="ri-layout-grid-line" aria-hidden="true" />
                频道广场
              </span>
              <h2 className="mt-2 font-display text-3xl font-bold text-foreground-900">挑一个频道开吃</h2>
            </div>
            <Link to="/rank" className="flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700">
              全网热议榜 <i className="ri-arrow-right-line" aria-hidden="true" />
            </Link>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {channels.map((c) => {
              const count = topics.filter((t) => t.channel === c.slug).length;
              return (
                <Link
                  key={c.slug}
                  to={`/channels/${c.slug}`}
                  className="group relative overflow-hidden rounded-2xl border border-background-200 bg-background-50"
                >
                  <div className="h-32 overflow-hidden">
                    <img
                      src={c.image}
                      alt={`${c.name}频道 - ${c.description}`}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-4">
                    <div className="flex items-center justify-between">
                      <h3 className="flex items-center gap-2 font-heading text-lg font-semibold text-foreground-900">
                        <span
                          className={`flex h-7 w-7 items-center justify-center rounded-md text-base ${
                            c.accent === "primary"
                              ? "bg-primary-100 text-primary-700"
                              : c.accent === "accent"
                                ? "bg-accent-100 text-accent-700"
                                : "bg-secondary-200 text-secondary-800"
                          }`}
                        >
                          <i className={c.icon} aria-hidden="true" />
                        </span>
                        {c.name}
                      </h3>
                      <i className="ri-arrow-right-up-line text-foreground-300 transition-colors group-hover:text-primary-600" aria-hidden="true" />
                    </div>
                    <p className="mt-1 text-xs text-foreground-400">{c.tagline}</p>
                    <p className="mt-3 line-clamp-2 text-sm leading-relaxed text-foreground-600">{c.description}</p>
                    <p className="mt-3 text-xs font-medium text-primary-600">{count} 个话题正在热议 →</p>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== 最新热议流 + 侧栏 ===== */}
      <section className="wrap mt-14 md:mt-16" id="feed">
        <div className="grid gap-8 lg:grid-cols-[1fr_320px]">
          <div>
            <div className="mb-6">
              <span className="flex items-center gap-2 text-sm font-bold text-primary-600">
                <i className="ri-chat-smile-3-line" aria-hidden="true" />
                新鲜讨论
              </span>
              <h2 className="mt-2 font-display text-3xl font-bold text-foreground-900">围观群众都在聊什么</h2>
            </div>

            {/* 分类切换 */}
            <div className="scrollbar-thin -mx-1 mb-6 flex gap-2 overflow-x-auto px-1 pb-1">
              <button
                type="button"
                onClick={() => setActive("all")}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  active === "all"
                    ? "bg-primary-500 font-bold text-background-50"
                    : "bg-background-100 text-foreground-600 hover:bg-background-200"
                }`}
              >
                全部
              </button>
              {channels.map((c) => (
                <button
                  key={c.slug}
                  type="button"
                  onClick={() => setActive(c.slug)}
                  className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                    active === c.slug
                      ? "bg-primary-500 font-bold text-background-50"
                      : "bg-background-100 text-foreground-600 hover:bg-background-200"
                  }`}
                >
                  {c.name}
                </button>
              ))}
            </div>

            <p className="mb-5 text-sm text-foreground-500">
              {active === "all" ? "为你挑选全部频道的最新讨论" : `正在围观「${activeCh?.name}」频道的 ${feed.length} 个话题`}
            </p>

            {feed.length === 0 && (
              <div className="card-tint rounded-2xl p-10 text-center text-sm text-foreground-500">
                这个频道的话题都上榜了，去 <Link to="/rank" className="font-semibold text-primary-600">热议榜</Link> 看看～
              </div>
            )}

            <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
              {feed.map((t, i) => (
                <TopicCard key={t.slug} topic={t} index={i} />
              ))}
            </div>
          </div>

          {/* 侧栏 */}
          <div className="space-y-5 lg:sticky lg:top-24 lg:self-start">
            <RankSidebar />
            <aside className="card-tint rounded-2xl bg-accent-100/60 p-5">
              <h3 className="font-display text-lg font-bold text-accent-900">
                <i className="ri-megaphone-line" aria-hidden="true" /> 吃瓜也要有态度
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-700">
                我们把多方声音都摆出来，不预设立场。理性围观、有热度更要有温度。
              </p>
              <Link
                to="/about"
                className="mt-4 flex items-center justify-center gap-1 whitespace-nowrap rounded-lg bg-accent-600 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-700"
              >
                了解我们的坚持
              </Link>
            </aside>
          </div>
        </div>
      </section>

      {/* ===== 金句墙 ===== */}
      <section className="mt-16 bg-background-100 py-14 md:py-16">
        <div className="wrap">
          <div className="mb-8 flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-end">
            <div>
              <span className="flex items-center gap-2 text-sm font-bold text-primary-600">
                <i className="ri-quill-pen-line" aria-hidden="true" />
                群众高赞
              </span>
              <h2 className="mt-2 font-display text-3xl font-bold text-foreground-900">金句 · 神评 · 神回复</h2>
            </div>
            <p className="text-sm text-foreground-500">这些留言，说出了很多人的心声</p>
          </div>

          <div className="scrollbar-thin -mx-1 flex gap-4 overflow-x-auto px-1 pb-2">
            {quotes.map((q, i) => (
              <figure
                key={i}
                className="w-72 shrink-0 rounded-2xl border border-background-200 bg-background-50 p-5 sm:w-80"
              >
                <i className="ri-double-quotes-l text-2xl text-primary-300" aria-hidden="true" />
                <blockquote className="mt-2 text-sm leading-relaxed text-foreground-700">{q.text}</blockquote>
                <figcaption className="mt-4 flex items-center gap-3 border-t border-background-200 pt-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary-200 text-sm font-bold text-secondary-800">
                    {q.author.slice(0, 1)}
                  </span>
                  <span>
                    <span className="block text-sm font-semibold text-foreground-800">{q.author}</span>
                    <span className="text-xs text-foreground-500">{q.role}</span>
                  </span>
                  <i className="ri-heart-3-fill ml-auto text-primary-400" aria-hidden="true" />
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section className="wrap mt-16 md:mt-20" id="faq">
        <div className="grid gap-10 lg:grid-cols-[1fr_1.2fr]">
          <div>
            <span className="flex items-center gap-2 text-sm font-bold text-primary-600">
              <i className="ri-question-answer-line" aria-hidden="true" />
              常见问题
            </span>
            <h2 className="mt-2 font-display text-3xl font-bold text-foreground-900">关于全网吃瓜</h2>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-foreground-600">
              我们不制造热点，只做热点的「翻译官」。把复杂讨论整理成你看得懂的舆论速览。
              有任何疑问，欢迎来下面找答案，或直接联系我们。
            </p>
            <Link
              to="/about"
              className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-lg border border-background-200 bg-background-50 px-5 py-3 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
            >
              了解更多品牌理念
              <i className="ri-arrow-right-line" aria-hidden="true" />
            </Link>
          </div>
          <FaqAccordion />
        </div>
      </section>

      <Footer />
      <ScrollTop />
    </div>
  );
}

function FeaturedTrend({ topic }: { topic: (typeof topics)[number] }) {
  return (
    <article className="group flex flex-col overflow-hidden rounded-2xl border border-background-200 bg-background-50 lg:row-span-2">
      <Link to={`/topics/${topic.slug}`} className="relative block h-52 overflow-hidden md:h-64">
        <img
          src={topic.image}
          alt={`${topic.title} - 今日舆论风向头条`}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <span className="absolute left-4 top-4 rounded-full bg-primary-500 px-3 py-1 text-xs font-bold text-background-50">
          <i className="ri-fire-fill" aria-hidden="true" /> 头条风向
        </span>
        <span className="absolute bottom-3 left-4 rounded-full bg-foreground-950/45 px-2.5 py-1 text-xs text-background-50 backdrop-blur-sm">
          热度 {formatNum(topic.heat)} · {formatNum(topic.comments)} 条讨论
        </span>
      </Link>
      <div className="flex flex-1 flex-col p-5 md:p-6">
        <h3 className="font-heading text-2xl font-bold leading-snug text-foreground-900">
          <Link to={`/topics/${topic.slug}`} className="transition-colors hover:text-primary-700">
            {topic.title}
          </Link>
        </h3>
        <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-foreground-600">{topic.conclusion}</p>
        <div className="mt-4 flex items-center gap-2 text-xs">
          {topic.mood.map((m) => (
            <span key={m} className="rounded-full bg-accent-100 px-2.5 py-1 font-medium text-accent-900">
              情绪 · {m}
            </span>
          ))}
        </div>
        <Link
          to={`/topics/${topic.slug}`}
          className="mt-6 inline-flex w-max items-center gap-1.5 whitespace-nowrap rounded-lg bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
        >
          围观舆论风向
          <i className="ri-arrow-right-line" aria-hidden="true" />
        </Link>
      </div>
    </article>
  );
}

function CompactTrend({ topic }: { topic: (typeof topics)[number] }) {
  return (
    <article className="group flex flex-col rounded-2xl border border-background-200 bg-background-50">
      <Link to={`/topics/${topic.slug}`} className="flex items-start gap-3 p-4">
        <span className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-accent-100 text-xs font-bold text-accent-700">
          <i className="ri-chat-heart-line" aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="line-clamp-2 text-sm font-semibold leading-snug text-foreground-900">
            <Link to={`/topics/${topic.slug}`} className="hover:text-primary-700">
              {topic.title}
            </Link>
          </h3>
          <div className="mt-2 flex h-2 w-full overflow-hidden rounded-full">
            <span className="bg-accent-500" style={{ width: `${topic.sentiment.support}%` }} />
            <span className="bg-secondary-300" style={{ width: `${topic.sentiment.neutral}%` }} />
            <span className="bg-primary-500" style={{ width: `${topic.sentiment.oppose}%` }} />
          </div>
          <div className="mt-2 flex items-center justify-between text-xs text-foreground-400">
            <span className="flex items-center gap-1">
              <i className="ri-flashlight-fill text-accent-500" aria-hidden="true" />
              {formatNum(topic.heat)}
            </span>
            <span className="text-primary-600">{topic.sentiment.oppose}% 反对</span>
          </div>
        </div>
      </Link>
    </article>
  );
}